import React, { useEffect, useState, useCallback } from "react";
import { auth, db } from "../firebase-config";
import { workSessionService } from "../services/workSessionService";
import { collection, getDocs, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./EmployeeHistory.css";

function EmployeeHistoryEnd() {
  const [sessions, setSessions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [sites, setSites] = useState([]);
  const [employeeName, setEmployeeName] = useState('');
  const navigate = useNavigate();

  const fetchHistory = useCallback(async (email) => {
    try {
      const sessionData = await workSessionService.getEmployeeHistory(email);
      setSessions(sessionData);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique :", error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchHistory(user.email);
        fetchSites();
        fetchEmployeeName(user.email);
      }
    });
    return () => unsubscribe();
  }, [fetchHistory]);

  const fetchEmployeeName = async (email) => {
    try {
      const querySnapshot = await getDocs(
        collection(db, "employees"),
        where("email", "==", email)
      );
      if (!querySnapshot.empty) {
        const employeeData = querySnapshot.docs[0].data();
        setEmployeeName(`${employeeData.prenom} ${employeeData.nom}`);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du nom de l'employé:", error);
    }
  };

  const fetchSites = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "sites"));
      const sitesList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setSites(sitesList);
    } catch (error) {
      console.error("Erreur lors de la récupération des sites:", error);
    }
  };

  const formatDate = (timestamp) => {
    try {
      if (timestamp?.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
      return "N/A";
    } catch (error) {
      console.warn('Erreur lors du formatage de la date:', error);
      return "N/A";
    }
  };

  const formatTimeHHMM = (timestamp) => {
    try {
      if (timestamp?.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return "N/A";
    } catch (error) {
      console.warn('Erreur lors du formatage de l\'heure:', error);
      return "N/A";
    }
  };

  const formatTime = (milliseconds) => {
    if (!milliseconds) return "0h 0m";
    const totalMinutes = Math.round(milliseconds / 60000); // Arrondi à la minute la plus proche
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const getFilteredSessions = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return sessions.filter(session => {
      try {
        if (!session.startTime?.seconds) return false;
        const sessionDate = new Date(session.startTime.seconds * 1000);
        const sessionMonth = sessionDate.getMonth();
        const sessionYear = sessionDate.getFullYear();
        
        if (selectedMonth === 'current') {
          return sessionMonth === currentMonth && sessionYear === currentYear;
        } else {
          const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
          const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
          return sessionMonth === previousMonth && sessionYear === previousYear;
        }
      } catch (error) {
        console.warn('Erreur lors du traitement de la session:', session, error);
        return false;
      }
    }).sort((a, b) => {
      try {
        return (b.startTime?.seconds || 0) - (a.startTime?.seconds || 0);
      } catch (error) {
        console.warn('Erreur lors du tri des sessions:', error);
        return 0;
      }
    });
  };

  const getSiteNames = (siteIds) => {
    if (!siteIds) return "N/A";
    const siteIdArray = Array.isArray(siteIds) ? siteIds : [siteIds];
    return siteIdArray
      .map(siteId => {
        const site = sites.find(s => s.id === siteId);
        return site ? site.nom : "Site inconnu";
      })
      .join(", ");
  };

  const handleBack = () => {
    sessionStorage.setItem('showRecap', '1');
    navigate("/");
  };

  const filteredSessions = getFilteredSessions();

  return (
    <div className="history-container">
      <button 
        className="back-button" 
        onClick={handleBack}
      >
        ⏰ Retour
      </button>

      <h1 className="page-title">Historique de {employeeName}</h1>

      <div className="month-buttons">
        <button 
          className={`month-button ${selectedMonth === 'current' ? 'active' : ''}`}
          onClick={() => setSelectedMonth('current')}
        >
          Mois en cours
        </button>
        <button 
          className={`month-button ${selectedMonth === 'previous' ? 'active' : ''}`}
          onClick={() => setSelectedMonth('previous')}
        >
          Mois précédent
        </button>
      </div>

      <table className="session-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Heure de début</th>
            <th>Heure de fin</th>
            <th>Temps de travail</th>
            <th>Pause</th>
            <th>Site</th>
          </tr>
        </thead>
        <tbody>
          {filteredSessions.map((session, index) => (
            <tr key={index}>
              <td>{formatDate(session.startTime)}</td>
              <td>{formatTimeHHMM(session.startTime)}</td>
              <td>{formatTimeHHMM(session.endTime)}</td>
              <td>{formatTime(
                session.adjustedWorkTime != null
                  ? session.adjustedWorkTime
                  : (session.endTime?.seconds && session.startTime?.seconds)
                    ? Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0))
                    : 0
              )}</td>
              <td>{formatTime(session.totalPauseTime)}</td>
              <td>{getSiteNames(session.siteDeTravail)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="3" style={{ textAlign: "right" }}>Total :</td>
            <td>
              {formatTime(
                filteredSessions.reduce((total, session) =>
                  total + (session.adjustedWorkTime != null
                    ? session.adjustedWorkTime
                    : (session.endTime?.seconds && session.startTime?.seconds)
                      ? Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0))
                      : 0), 0)
              )}
            </td>
            <td>
              {formatTime(
                filteredSessions.reduce((total, session) => 
                  total + (session.totalPauseTime || 0), 0
                )
              )}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default EmployeeHistoryEnd; 